/**
 * Asset-type discrimination for the shared `vehicles` model.
 *
 * Requests have no vehicle FK — the request→asset link is the VIN *string*.
 * Non-automotive assets therefore carry a synthetic prefixed VIN:
 *   NV = automotive, VIN not collected
 *   OT = "Other car" / non-vehicle
 *   BT = boat (marine)
 *
 * ZERO IMPORTS BY DESIGN — this module is unit-tested directly by
 * `scripts/test/assetType.test.mjs` under Node's ESM syntax detection.
 * Adding any import breaks that harness. Use native Date, not moment.
 */

export const ASSET_TYPES = { AUTO: 'auto', MARINE: 'marine', OTHER: 'other' };

/**
 * `vehicle.asset_type` is ENUM('car','boat') — a DIFFERENT value set from the
 * one above, and from `master_services.asset_type` ENUM('auto','marine').
 *
 * The column name is shared; the vocabularies are not:
 *
 *   vehicle.asset_type          car | boat     the STRUCTURE of the asset
 *   master_services.asset_type  auto | marine  the MARKET a service sells into
 *
 * `marine` is correct on a SERVICE. On a VEHICLE the value is `boat`. Sending
 * `marine` here is not a near-miss that degrades gracefully — the server's
 * "unknown asset_type degrades to car" rule (written for pre-marine callers that
 * send nothing) swallows it and writes a boat as a car, with HTTP 200 and no
 * marine record at all. That is how vehicles 3481 and 3489 were lost.
 */
export const VEHICLE_ASSET_TYPE = { CAR: 'car', BOAT: 'boat' };

/** The `vehicle.asset_type` value for a VIN. Boats are BT-prefixed. */
export function vehicleAssetType(vin) {
  return isMarine(vin) ? VEHICLE_ASSET_TYPE.BOAT : VEHICLE_ASSET_TYPE.CAR;
}

/**
 * Is this vehicle a boat? Tolerant of every spelling in play:
 * the `boat` the server stores, the `marine` the client used to send, and the
 * BT VIN prefix — which is authoritative when asset_type is absent or wrong.
 */
export function isMarineVehicle(vehicle) {
  const v = vehicle || {};
  const t = String(v.asset_type ?? v.assetType ?? '').trim().toLowerCase();
  if (t === VEHICLE_ASSET_TYPE.BOAT || t === ASSET_TYPES.MARINE) return true;
  // The VIN field name varies by read. `vehicles` rows use `vin`; the
  // customer-vehicle list keys on `VIN`; job rows carry `Vin` or `vehicle_vin`
  // — newCustomers.jsx:385 hedges across all four itself. Accept every
  // spelling; a missed one reads as "this is a car" and shows no boat marker.
  return isMarine(v.vin ?? v.VIN ?? v.Vin ?? v.vehicle_vin);
}

export const VIN_PREFIX = { NOT_COLLECTED: 'NV', OTHER: 'OT', BOAT: 'BT' };

/** Trim + uppercase a VIN for safe comparison / URL use. */
export function normalizeVin(vin) {
  return String(vin ?? '').trim().toUpperCase();
}

/**
 * NV is an automotive vehicle whose VIN was never collected — still a car.
 * Only BT denotes a different asset type.
 */
export function getAssetType(vin) {
  const v = normalizeVin(vin);
  if (v.startsWith(VIN_PREFIX.BOAT)) return ASSET_TYPES.MARINE;
  if (v.startsWith(VIN_PREFIX.OTHER)) return ASSET_TYPES.OTHER;
  return ASSET_TYPES.AUTO;
}

export function isMarine(vin) {
  return getAssetType(vin) === ASSET_TYPES.MARINE;
}

/**
 * BROAD predicate: every synthetic or absent VIN. Use where all placeholders
 * must be suppressed — duplicate detection, the invoicing VIN gate.
 * Replaces sites that today test `NV || OT`.
 */
export function isPlaceholderVin(vin) {
  const v = normalizeVin(vin);
  return v === '' ||
    v.startsWith(VIN_PREFIX.NOT_COLLECTED) ||
    v.startsWith(VIN_PREFIX.OTHER) ||
    v.startsWith(VIN_PREFIX.BOAT);
}

/**
 * NARROW predicate: "no real VIN to show for this asset" — NV or BT, but NOT
 * OT and NOT empty. Preserves the truth table of the ~12 legacy call sites
 * that test `NV` only, while making them BT-aware.
 */
export function isUncollectedVin(vin) {
  const v = normalizeVin(vin);
  return v.startsWith(VIN_PREFIX.NOT_COLLECTED) || v.startsWith(VIN_PREFIX.BOAT);
}

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * Mint a synthetic VIN: 2-char prefix + YYYYMMDDHHmmss (16 chars).
 * `now` is injectable for tests.
 */
export function mintVin(assetType = ASSET_TYPES.AUTO, now = new Date()) {
  const stamp = `${now.getFullYear()}${pad2(now.getMonth() + 1)}${pad2(now.getDate())}` +
    `${pad2(now.getHours())}${pad2(now.getMinutes())}${pad2(now.getSeconds())}`;
  const prefix =
    assetType === ASSET_TYPES.MARINE ? VIN_PREFIX.BOAT :
    assetType === ASSET_TYPES.OTHER ? VIN_PREFIX.OTHER :
    VIN_PREFIX.NOT_COLLECTED;
  return prefix + stamp;
}

/**
 * Asset-type-aware display label.
 *
 * Accepts BOTH field shapes: a `vehicles` row (year/make/model/trim) and a
 * `requests` row (vehicleYear/vehicleMake/vehicleModel/vehicleTrim). They are
 * genuinely different in the wire data — displayJob.js:78-85 reads the second.
 * Never throws on null. Colour is deliberately excluded: it uses a different
 * separator (" - ") at its one call site, which keeps appending it itself.
 */
export function getAssetLabel(source) {
  const s = source || {};
  const parts = [
    s.year ?? s.vehicleYear,
    s.make ?? s.vehicleMake,
    s.model ?? s.vehicleModel,
    s.trim ?? s.vehicleTrim,
  ].filter((p) => p !== null && p !== undefined && String(p).trim() !== '');
  if (parts.length) return parts.join(' ');
  return isMarine(s.vin) ? 'Boat' : 'Vehicle';
}
